import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { reportError } from '../../lib/monitoring';
import type { CompanyBrandingRow } from '../database.types';

/**
 * Saved company branding — M10 / S10-03A.
 *
 * UI → THIS HOOK → `.select()` (ARCHITECTURE §2). **This hook writes nothing.**
 *
 * AUTHORIZATION IS RLS. `company_branding_select_own` matches only the caller's
 * own row, under forced row-level security, so another account's branding is
 * unreachable regardless of what is asked for.
 *
 * **THE COLUMNS ARE NAMED, AND THAT IS THE WHOLE REASON THIS FILE HAS TWO
 * READS.** `logo_base64` is the only large column in the schema — a couple of
 * hundred kilobytes. Every other query in this app uses `select('*')`, and
 * following that habit here would put the logo on the wire for any screen that
 * merely wanted a company name. So the default read asks for the metadata and
 * **deliberately omits the bytes**; `logo_mime` still tells a caller whether a
 * logo exists. Only a screen that actually renders the logo calls the second
 * read.
 */

/** Everything except the bytes. `logo_mime` answers "is there a logo?" without them. */
export type CompanyBrandingSummary = Omit<CompanyBrandingRow, 'logo_base64'>;

const SUMMARY_COLUMNS =
  'owner_user_id, company_name, logo_mime, contact_email, contact_phone, created_at, updated_at';

export type CompanyBrandingStatus = 'loading' | 'ready' | 'failed';

export interface CompanyBrandingState {
  status: CompanyBrandingStatus;
  /** Null is an ORDINARY state: branding has simply never been configured. */
  branding: CompanyBrandingSummary | null;
  error: string | null;
  reload: () => Promise<void>;
}

export function useCompanyBranding(): CompanyBrandingState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<CompanyBrandingStatus>(client ? 'loading' : 'failed');
  const [branding, setBranding] = useState<CompanyBrandingSummary | null>(null);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );

  /**
   * The read can finish after the screen is gone — Brandon opens Settings and
   * immediately goes back. Setting state then is a React warning and, worse, a
   * write into a screen nobody is looking at. Same guard as the photo board.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * **THE READ AND THE APPLY ARE SEPARATE, MATCHING `useCaptures`.** Fetching
   * returns a result; applying it is the only thing that touches state. Beyond
   * following the house pattern, it keeps the network step testable without a
   * rendered component.
   */
  const fetchBranding = useCallback(async (): Promise<
    { kind: 'ready'; row: CompanyBrandingSummary | null } | { kind: 'failed' }
  > => {
    if (!client) return { kind: 'failed' };
    const { data, error: queryError } = await client
      .from('company_branding')
      .select(SUMMARY_COLUMNS)
      .maybeSingle();

    if (queryError) {
      reportError(queryError, 'branding.load');
      return { kind: 'failed' };
    }
    // **NOT CONFIGURED IS NOT A FAILURE.** Brandon can use Crewlink and send
    // unbranded reports without ever opening this screen.
    return { kind: 'ready', row: data };
  }, [client]);

  const apply = useCallback(
    (result: { kind: 'ready'; row: CompanyBrandingSummary | null } | { kind: 'failed' }) => {
      if (!alive.current) return;
      if (result.kind === 'failed') {
        setBranding(null);
        setError('Could not load your company branding.');
        setStatus('failed');
        return;
      }
      setBranding(result.row);
      setError(null);
      setStatus('ready');
    },
    [],
  );

  /**
   * **ONLY THE NEWEST READ MAY SPEAK.** A save triggers a reload while the
   * first load may still be in flight, and answers do not come back in the
   * order they left — an older one landing last would put stale branding on
   * screen with nothing to suggest anything was wrong. Every read takes a
   * ticket; a superseded read is allowed to finish and not to write. This is
   * the same guard `useCaptures` uses, for the same reason.
   */
  const generation = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    generation.current += 1;
    const ticket = generation.current;
    const result = await fetchBranding();
    if (ticket !== generation.current) return;
    apply(result);
  }, [fetchBranding, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  return { status, branding, error, reload };
}

/**
 * The logo bytes, fetched ONLY by a screen that draws them.
 *
 * Kept apart from the summary read on purpose: this is the expensive half, and
 * a caller has to ask for it by name.
 */
export async function readCompanyLogo(): Promise<string | null> {
  const client = getSupabaseClient();
  if (!client) return null;
  const { data, error } = await client.from('company_branding').select('logo_base64').maybeSingle();
  if (error) {
    reportError(error, 'branding.logo');
    return null;
  }
  return data?.logo_base64 ?? null;
}
